import React from 'react';
import { Helmet } from 'react-helmet-async';
import { VerifiedUser, Security, Assessment, CheckCircle, Lock, Shield } from '@mui/icons-material';

const Compliance = () => {
    const certifications = [
        {
            title: 'PCI DSS Level 1',
            description: 'Niveau le plus élevé de certification PCI DSS pour le traitement sécurisé des données de paiement',
            icon: VerifiedUser,
            color: 'bg-green-100 text-green-700'
        },
        {
            title: 'RGPD',
            description: 'Conformité au Règlement Général sur la Protection des Données de l\'Union Européenne',
            icon: Security,
            color: 'bg-blue-100 text-blue-700'
        },
        {
            title: 'ISO 27001',
            description: 'Système de gestion de la sécurité de l\'information certifié',
            icon: Shield,
            color: 'bg-purple-100 text-purple-700'
        }
    ];

    const securityMeasures = [
        {
            category: 'Chiffrement',
            items: [
                'Chiffrement TLS 1.3 pour toutes les communications',
                'Chiffrement AES-256 pour les données au repos',
                'Gestion sécurisée des clés de chiffrement',
                'Certificats SSL/TLS à jour'
            ]
        },
        {
            category: 'Accès et authentification',
            items: [
                'Authentification multi-facteurs (MFA) obligatoire',
                'Gestion des identités et accès (IAM)',
                'Principe du moindre privilège',
                'Audit des accès régulier'
            ]
        },
        {
            category: 'Surveillance et détection',
            items: [
                'Surveillance 24/7 des systèmes',
                'Détection d\'intrusions en temps réel',
                'Logging et monitoring complets',
                'Alertes automatiques pour anomalies'
            ]
        },
        {
            category: 'Infrastructure',
            items: [
                'Hébergement sécurisé avec redondance',
                'Sauvegardes automatiques quotidiennes',
                'Plans de reprise après sinistre',
                'Tests de pénétration réguliers'
            ]
        }
    ];

    return (
        <>
            <Helmet>
                <title>Conformité et Certifications - Netikash Developer Portal</title>
                <meta name="description" content="Certifications et conformité Netikash : PCI DSS Level 1, RGPD, ISO 27001. Mesures de sécurité et audits réguliers." />
                <meta property="og:title" content="Conformité et Certifications - Netikash" />
                <meta property="og:description" content="Découvrez les certifications de sécurité et de conformité de Netikash." />
                <link rel="canonical" href="https://dev.netikash.com/compliance" />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                                <VerifiedUser className="mr-2" />
                                <span className="font-semibold text-lg">CONFORMITÉ & SÉCURITÉ</span>
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                                Certifications et Conformité
                            </h1>
                            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                                Netikash maintient les plus hauts standards de sécurité et de conformité pour protéger vos données et celles de vos clients.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Certifications */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Certifications</h2>
                        <div className="grid md:grid-cols-3 gap-6">
                            {certifications.map((cert, index) => {
                                const Icon = cert.icon;
                                return (
                                    <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
                                        <div className={`w-16 h-16 ${cert.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                                            <Icon className="text-3xl" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 mb-3">{cert.title}</h3>
                                        <p className="text-gray-700">{cert.description}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* PCI DSS */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12">
                        <div className="flex items-center space-x-4 mb-6">
                            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                                <VerifiedUser className="text-green-600 text-3xl" />
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900">PCI DSS Level 1</h2>
                                <p className="text-gray-600">Certification de niveau 1 - La plus stricte</p>
                            </div>
                        </div>
                        <div className="space-y-6 text-gray-700">
                            <p className="text-lg">
                                Netikash est certifié <strong>PCI DSS Level 1</strong>, le niveau de conformité le plus élevé pour les organisations qui traitent, stockent ou transmettent des données de cartes de paiement.
                            </p>
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                                    <h3 className="font-semibold text-green-900 mb-3">Ce que cela signifie :</h3>
                                    <ul className="space-y-2 text-green-800">
                                        <li className="flex items-start space-x-2">
                                            <CheckCircle className="text-green-600 mt-0.5" fontSize="small" />
                                            <span>Validation annuelle par un auditeur qualifié (QSA)</span>
                                        </li>
                                        <li className="flex items-start space-x-2">
                                            <CheckCircle className="text-green-600 mt-0.5" fontSize="small" />
                                            <span>Conformité aux 12 exigences PCI DSS</span>
                                        </li>
                                        <li className="flex items-start space-x-2">
                                            <CheckCircle className="text-green-600 mt-0.5" fontSize="small" />
                                            <span>Protection maximale des données de paiement</span>
                                        </li>
                                        <li className="flex items-start space-x-2">
                                            <CheckCircle className="text-green-600 mt-0.5" fontSize="small" />
                                            <span>Rapports de conformité (ROC) réguliers</span>
                                        </li>
                                    </ul>
                                </div>
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                                    <h3 className="font-semibold text-blue-900 mb-3">Avantages pour vous :</h3>
                                    <ul className="space-y-2 text-blue-800">
                                        <li className="flex items-start space-x-2">
                                            <CheckCircle className="text-blue-600 mt-0.5" fontSize="small" />
                                            <span>Confiance accrue de vos clients</span>
                                        </li>
                                        <li className="flex items-start space-x-2">
                                            <CheckCircle className="text-blue-600 mt-0.5" fontSize="small" />
                                            <span>Conformité réglementaire simplifiée</span>
                                        </li>
                                        <li className="flex items-start space-x-2">
                                            <CheckCircle className="text-blue-600 mt-0.5" fontSize="small" />
                                            <span>Protection contre les violations de données</span>
                                        </li>
                                        <li className="flex items-start space-x-2">
                                            <CheckCircle className="text-blue-600 mt-0.5" fontSize="small" />
                                            <span>Conformité avec les standards de l'industrie</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Mesures de sécurité */}
                    <section className="mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Mesures de Sécurité</h2>
                        <div className="grid md:grid-cols-2 gap-6">
                            {securityMeasures.map((measure, index) => (
                                <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                                    <div className="flex items-center space-x-3 mb-4">
                                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                                            <Lock className="text-indigo-600" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900">{measure.category}</h3>
                                    </div>
                                    <ul className="space-y-2">
                                        {measure.items.map((item, idx) => (
                                            <li key={idx} className="flex items-start space-x-2 text-gray-700">
                                                <CheckCircle className="text-green-500 mt-0.5" fontSize="small" />
                                                <span className="text-sm">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Audits et certifications */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12">
                        <div className="flex items-center space-x-4 mb-6">
                            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                                <Assessment className="text-purple-600" />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Audits et Certifications</h2>
                        </div>
                        <div className="space-y-6 text-gray-700">
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-3">Audits de sécurité</h3>
                                <ul className="list-disc list-inside space-y-2 ml-4">
                                    <li>Audits PCI DSS annuels par un auditeur qualifié (QSA)</li>
                                    <li>Tests de pénétration trimestriels</li>
                                    <li>Audits de code sécurité réguliers</li>
                                    <li>Vulnérabilité scanning continu</li>
                                </ul>
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-3">Certifications maintenues</h3>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <p className="font-semibold text-gray-900">PCI DSS Level 1</p>
                                        <p className="text-sm text-gray-600">Validé annuellement</p>
                                    </div>
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <p className="font-semibold text-gray-900">RGPD</p>
                                        <p className="text-sm text-gray-600">Conformité continue</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Conformité réglementaire */}
                    <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 mb-12">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">Conformité Réglementaire</h2>
                        <div className="space-y-6 text-gray-700">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="border border-gray-200 rounded-lg p-6">
                                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                                        <Security className="text-blue-600 mr-2" />
                                        Protection des données
                                    </h3>
                                    <ul className="space-y-2 text-sm">
                                        <li>• Conformité RGPD (UE)</li>
                                        <li>• Respect des lois locales sur la protection des données</li>
                                        <li>• Transferts de données sécurisés</li>
                                        <li>• Droit à l'effacement des données</li>
                                    </ul>
                                </div>
                                <div className="border border-gray-200 rounded-lg p-6">
                                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                                        <Shield className="text-green-600 mr-2" />
                                        Standards financiers
                                    </h3>
                                    <ul className="space-y-2 text-sm">
                                        <li>• Conformité aux réglementations financières</li>
                                        <li>• Lutte contre le blanchiment d'argent (AML)</li>
                                        <li>• Vérification des identités (KYC)</li>
                                        <li>• Rapports réglementaires</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Rapport de conformité */}
                    <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-8">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4">Documentation de Conformité</h2>
                            <p className="text-gray-700 mb-6">
                                Les clients enterprise peuvent demander des documents de conformité, des rapports d'audit ou des attestations PCI DSS.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <a
                                    href="mailto:compliance@netikash.com"
                                    className="px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all inline-flex items-center justify-center"
                                >
                                    Demander des documents
                                </a>
                                <a
                                    href="/security"
                                    className="px-6 py-3 bg-white border border-amber-300 text-amber-700 rounded-lg font-medium hover:bg-amber-50 transition-all inline-flex items-center justify-center"
                                >
                                    En savoir plus sur la sécurité
                                </a>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default Compliance;